import { User } from 'src/types/auth';

export const parseAuthId = (id?: string | number | null): number | null => {
  if (id == null) {
    return null;
  }
  const parsed = Number(id);
  return Number.isNaN(parsed) ? null : parsed;
};

export const isImpersonatingSession = (
  loggedInUserId?: string | number | null,
  clientId?: string | number | null
): boolean => {
  if (loggedInUserId == null || clientId == null) {
    return false;
  }
  return String(loggedInUserId) !== String(clientId);
};

type ImpersonationUser = Pick<User, 'LoggedInUserId' | 'ClientId' | 'CompanyId'> | null | undefined;

export const isUserImpersonating = (user: ImpersonationUser): boolean =>
  isImpersonatingSession(user?.LoggedInUserId, user?.ClientId);

export const getImpersonatedCompanyIdFromUser = (user: ImpersonationUser): number | null => {
  if (!isUserImpersonating(user) || user?.CompanyId == null) {
    return null;
  }
  return parseAuthId(user.CompanyId);
};

export const getImpersonatedClientIdFromUser = (user: ImpersonationUser): number | null => {
  if (!isUserImpersonating(user) || user?.ClientId == null) {
    return null;
  }
  return parseAuthId(user.ClientId);
};

export const getSessionClientId = (clientId?: string | number | null): number | null =>
  parseAuthId(clientId);

/**
 * Session behaves like Super Admin when:
 * - the JWT role contains IsSuperAdmin, OR
 * - the user's loggedInCompanyId is present in the super-admins list returned by
 *   GET /api/Client/GetSuperAdmins (covers impersonation: the impersonated token's
 *   isSuperAdmin is false, but LoggedInCompanyId still identifies the originating
 *   super-admin company).
 */
export const isSuperAdminSession = (
  isSuperAdmin: boolean,
  loggedInCompanyId: string | number | null | undefined,
  superAdminCompanyIds: number[]
): boolean => {
  if (isSuperAdmin) return true;
  const id = parseAuthId(loggedInCompanyId);
  return id != null && superAdminCompanyIds.includes(id);
};

/** True when the row belongs to the active session user (token ClientId). */
export const isSessionClientId = (
  rowClientId: number | null | undefined,
  sessionClientId: number | null
): boolean => {
  if (sessionClientId == null || rowClientId == null) {
    return false;
  }
  return Number(rowClientId) === sessionClientId;
};

/** @deprecated Use isSessionClientId with sessionClientId from auth context. */
export const isImpersonatedClientId = isSessionClientId;
